/**
 * Tournament ladder: a gauntlet of escalating-difficulty opponents the player
 * faces in sequence toward a championship. Pure data + builder (no Phaser).
 */
import { ROSTER, typeAdvantage, getBeyDef } from './stats.ts';

export type GameMode = 'exhibition' | 'tournament' | 'versus';

export interface LadderStage {
  opponentId: string;
  /** AI difficulty for this stage (0..1). */
  difficulty: number;
}

export interface TournamentRun {
  playerId: string;
  stages: LadderStage[];
  /** Index of the current (next-to-play) stage. */
  current: number;
  /** False once the player has lost a match. */
  alive: boolean;
  /** Hard mode ("Champion's Gauntlet"): every stage's difficulty is boosted. */
  hard: boolean;
}

/** Difficulty added to every stage in hard mode. */
export const HARD_BOOST = 0.25;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Build the ladder for a chosen bey: every other bey, ordered so the climactic
 * foe (the type the player is weak to) comes last, with difficulty ramping from
 * easy to hard across the stages.
 */
export function buildLadder(playerId: string, boost = 0): LadderStage[] {
  const player = getBeyDef(playerId);
  const others = ROSTER.filter((b) => b.id !== playerId);

  // Sort by how dangerous each is to the player (their type advantage over us),
  // so the hardest matchup is the final boss.
  const ordered = [...others].sort(
    (a, b) =>
      typeAdvantage(a.type, player.type, 1.3, 0.8) - typeAdvantage(b.type, player.type, 1.3, 0.8),
  );

  const n = ordered.length;
  return ordered.map((bey, i) => ({
    opponentId: bey.id,
    difficulty: Number(
      Math.min(1, lerp(0.45, 0.95, n <= 1 ? 1 : i / (n - 1)) + boost).toFixed(2),
    ),
  }));
}

export function newRun(playerId: string, hard = false): TournamentRun {
  return {
    playerId,
    stages: buildLadder(playerId, hard ? HARD_BOOST : 0),
    current: 0,
    alive: true,
    hard,
  };
}

export const isChampion = (run: TournamentRun): boolean =>
  run.alive && run.current >= run.stages.length;

/** Apply a match result, advancing the run or ending it on a loss. */
export function advanceRun(run: TournamentRun, playerWon: boolean): TournamentRun {
  if (!playerWon) return { ...run, alive: false };
  return { ...run, current: run.current + 1 };
}
